import React, { useState } from "react";
import axios from "axios";
import { Phone, Mail, ArrowRight, Loader2 } from "lucide-react";
import { useLang, CONTACT } from "@/i18n";
import { Reveal } from "@/lib/motion";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "@/components/ui/sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Contact() {
  const { t } = useLang();
  const c = t.contact;
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", message: "" });
  const [loading, setLoading] = useState(false);

  const change = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success(c.form.success);
      setForm({ name: "", email: "", phone: "", subject: "", message: "" });
    } catch (err) {
      toast.error(c.form.error);
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "min-h-[48px] bg-bone border-pine-300/50 focus:border-terracotta text-pine-900 placeholder:text-pine-300 rounded-xl";

  return (
    <section id="contact" className="py-24 md:py-32 bg-pine-900 text-bone" data-testid="contact-section">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <div className="grid lg:grid-cols-2 gap-14 lg:gap-20">
          <div>
            <Reveal><span className="inline-block text-xs uppercase tracking-[0.22em] font-bold text-terracotta mb-4">{c.overline}</span></Reveal>
            <Reveal delay={0.05}>
              <h2 className="font-serif font-light text-bone text-4xl md:text-6xl leading-tight tracking-tight mb-6">{c.title}</h2>
            </Reveal>
            <Reveal delay={0.1}><p className="text-lg leading-relaxed text-sage max-w-md mb-12">{c.body}</p></Reveal>

            <div className="space-y-4">
              <Reveal delay={0.12}>
                <a href={CONTACT.phoneHref} className="group flex items-center gap-4 rounded-2xl border border-pine-800 bg-pine-800/40 hover:bg-pine-800 p-5 transition-colors duration-300" data-testid="contact-phone">
                  <span className="grid place-items-center w-12 h-12 rounded-full bg-terracotta text-white shrink-0"><Phone className="w-5 h-5" /></span>
                  <span>
                    <span className="block text-xs uppercase tracking-widest text-sage">{c.phoneLabel}</span>
                    <span className="block text-lg font-semibold text-bone">{CONTACT.phone}</span>
                  </span>
                </a>
              </Reveal>
              <Reveal delay={0.16}>
                <a href={`mailto:${CONTACT.email}`} className="group flex items-center gap-4 rounded-2xl border border-pine-800 bg-pine-800/40 hover:bg-pine-800 p-5 transition-colors duration-300" data-testid="contact-email">
                  <span className="grid place-items-center w-12 h-12 rounded-full bg-terracotta text-white shrink-0"><Mail className="w-5 h-5" /></span>
                  <span>
                    <span className="block text-xs uppercase tracking-widest text-sage">{c.emailLabel}</span>
                    <span className="block text-lg font-semibold text-bone break-all">{CONTACT.email}</span>
                  </span>
                </a>
              </Reveal>
            </div>
          </div>

          <Reveal delay={0.1}>
            <form onSubmit={submit} className="rounded-[2rem] bg-bone text-pine-900 p-7 md:p-9" data-testid="contact-form">
              <div className="grid sm:grid-cols-2 gap-5">
                <div className="space-y-2">
                  <Label htmlFor="name" className="text-pine-800 font-semibold">{c.form.name}</Label>
                  <Input id="name" name="name" value={form.name} onChange={change} required className={inputCls} data-testid="contact-input-name" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-pine-800 font-semibold">{c.form.email}</Label>
                  <Input id="email" name="email" type="email" value={form.email} onChange={change} required className={inputCls} data-testid="contact-input-email" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="phone" className="text-pine-800 font-semibold">{c.form.phone}</Label>
                  <Input id="phone" name="phone" value={form.phone} onChange={change} className={inputCls} data-testid="contact-input-phone" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="subject" className="text-pine-800 font-semibold">{c.form.subject}</Label>
                  <Input id="subject" name="subject" value={form.subject} onChange={change} className={inputCls} data-testid="contact-input-subject" />
                </div>
              </div>
              <div className="space-y-2 mt-5">
                <Label htmlFor="message" className="text-pine-800 font-semibold">{c.form.message}</Label>
                <Textarea id="message" name="message" value={form.message} onChange={change} required rows={5} className="bg-bone border-pine-300/50 focus:border-terracotta text-pine-900 placeholder:text-pine-300 rounded-xl resize-none" data-testid="contact-input-message" />
              </div>
              <button
                type="submit"
                disabled={loading}
                className="group mt-7 w-full inline-flex items-center justify-center gap-2 rounded-full bg-terracotta hover:bg-terracotta-600 disabled:opacity-70 text-white px-7 py-4 text-base font-semibold transition-colors duration-200"
                data-testid="contact-submit"
              >
                {loading ? (
                  <><Loader2 className="w-5 h-5 animate-spin" /> {c.form.sending}</>
                ) : (
                  <>{c.form.submit} <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" /></>
                )}
              </button>
            </form>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
